<template>
  <!-- No content padding: the header line, the side columns and the footer bar run edge to edge. -->
  <BaseModal
    :modalTitle="$t('W: Beskrivelser til rapporten')"
    contentPadding="none">
    <!-- The modal host leaves 80vh for the whole dialog; its title takes about 6rem of that. -->
    <div class="flex h-[calc(80vh-6rem)] flex-col border-t border-border-subtle">
      <div
        v-if="isLoadingResults"
        class="px-6 py-6 text-sm text-text-muted">
        {{ $t('W: Henter prøver...') }}
      </div>
      <div
        v-else-if="states.length === 0"
        class="px-6 py-6 text-sm text-text-muted">
        {{ $t('W: Der er ingen prøver med resultater at beskrive endnu') }}
      </div>
      <div
        v-else
        class="flex min-h-0 flex-1">
        <!-- Left: the samples, each a card with how many descriptions it has -->
        <div class="flex w-72 shrink-0 flex-col gap-3 overflow-y-auto border-r border-border-subtle bg-surface-muted px-4 py-5">
          <span class="flex h-6 items-center px-1 text-xs font-semibold tracking-wide text-text-secondary uppercase">
            {{ $t('W: Prøver') }} ({{ states.length }})
          </span>
          <!-- The shared Card: interactive until it is the selected one, then the focus-bordered variant -->
          <Card
            v-for="state in states"
            :key="state.sampleID"
            :variant="state.sampleID === activeState?.sampleID ? 'selected' : 'interactive'"
            v-bind="state.sampleID === activeState?.sampleID ? { role: 'button' } : {}"
            class="flex flex-col gap-0.5 !px-3 !py-2"
            @on-click="onSampleClick(state.sampleID)">
            <span class="text-sm font-semibold text-text-primary">
              {{ $t('W: Prøve {number}', { number: state.sampleNumber }) }}
            </span>
            <span class="text-xs text-text-muted">
              {{ formatDescriptionCount(state.itemIDs.length) }}
            </span>
            <span
              v-if="state.analysisNames.length > 0"
              class="text-xs text-text-muted">
              {{ state.analysisNames.join(', ') }}
            </span>
          </Card>
        </div>

        <template v-if="activeState">
          <!-- Middle and right: find a description, and what the sample gets, in report order -->
          <ReportDescriptionsPicker
            :selectedItemIDs="activeState.itemIDs"
            :suggestedItemIDs="activeState.autoItemIDs"
            :reportLocale="reportLocale"
            :selectedChipLabel="$t('W: Automatisk')"
            :subtitle="$t('W: Knyttet til Prøve {number}', { number: activeState.sampleNumber })"
            @on-change="setFinalList" />
        </template>
      </div>

      <!-- Fixed bottom bar: the buttons, and an error if saving failed -->
      <div class="flex shrink-0 items-center justify-between gap-4 border-t border-border-subtle px-6 py-4">
        <div class="flex min-w-0 flex-1 flex-col gap-1">
          <ReportLanguageNotice
            :reportLocale="reportLocale"
            :itemIDs="allChosenItemIDs" />
          <p
            v-if="errorText"
            class="text-sm text-feedback-error">
            {{ errorText }}
          </p>
        </div>
        <div class="flex shrink-0 gap-2">
          <UiBaseButton
            :text="$t('action.cancel')"
            theme="neutral"
            @click="$emit('close')" />
          <UiBaseButton
            :text="$t('action.save')"
            :isLoading="isSaving"
            :isDisabled="changedSampleIDs.length === 0"
            :showSaveIcon="showSaveIcon"
            @click="onConfirm" />
        </div>
      </div>
    </div>
  </BaseModal>
</template>

<script lang="ts">
import { mapActions, mapState } from 'pinia'
import { isEqual } from 'lodash-es'
import { buildSampleDescriptionOverride } from '@team-milva/lab-models/resources'
import type {
  IAnalysisResult,
  ISampleDescriptionOverride,
} from '@team-milva/lab-models/resources'
import { useToast } from '@/globals/typescript/utils/toast'
import { reportUnexpectedError } from '@/globals/typescript/utils/reportError'

import { useUnsavedChangesGuard } from '@/composables/useUnsavedChangesGuard'
import { fetchOrderAnalysisResults } from '@/globals/typescript/utils/fetchOrderAnalysisResults'
import {
  resolveReportSampleDescriptionStates,
  type IReportSampleDescriptionState,
} from '@/globals/typescript/utils/orderReportSampleDescriptions'
import { useAppStore } from '@/stores/appStore'
import { useAccountStore } from '@/stores/accountStore'
import { useModalStore } from '@/stores/modalStore'
import { useOrderStore } from '@/stores/orderStore'
import { useAnalysisStore } from '@/stores/analysisStore'
import { useAnalysisTestStore } from '@/stores/analysisTestStore'
import { useTextListStore } from '@/stores/textListStore'
import { useReportDescriptionStore } from '@/stores/reportDescriptionStore'
import { useReportDescriptionItemStore } from '@/stores/reportDescriptionItemStore'

/** An override that changes nothing is the same as no override. */
function normalizeOverride(
  override: ISampleDescriptionOverride | undefined
): ISampleDescriptionOverride | null {
  if (!override) return null
  const isEmpty =
    override.removedItemIDs.length === 0 &&
    override.addedItemIDs.length === 0 &&
    override.itemOrder === null
  return isEmpty ? null : override
}

export default {
  name: 'OrderReportSampleDescriptionsModal',

  emits: ['close'],

  props: {
    orderID: { type: String, required: true },
    tatGroupID: { type: String, required: true },
    reportTemplateID: { type: String, required: true },
  },

  data() {
    return {
      unsavedGuard: useUnsavedChangesGuard(),
      // Captured synchronously: created() awaits store loads before it registers, and by then
      // another modal can be the top entry.
      modalEntryID: useModalStore().top?.id,

      orderResults: [] as IAnalysisResult[],
      isLoadingResults: true,
      /** The user's unsaved override per sample — only samples they have touched. */
      drafts: {} as Record<string, ISampleDescriptionOverride>,
      activeSampleID: '',
      isSaving: false,
      showSaveIcon: false,
      errorText: '',
    }
  },

  computed: {
    ...mapState(useAppStore, ['currentLocale', 'defaultMessageSaveIconTime']),
    ...mapState(useAccountStore, ['labAccount']),
    ...mapState(useOrderStore, ['allOrders']),
    ...mapState(useAnalysisStore, ['allAnalyses']),
    ...mapState(useAnalysisTestStore, ['allAnalysisTests']),
    ...mapState(useTextListStore, ['systemLists']),
    ...mapState(useReportDescriptionStore, ['allReportDescriptions']),
    ...mapState(useReportDescriptionItemStore, ['allReportDescriptionItems']),

    order() {
      return this.allOrders.find((x) => x.id === this.orderID)
    },

    // The language the report is printed in — the order's, as in the preview and the PDF
    reportLocale(): string {
      return this.order?.reportLocale || this.currentLocale
    },

    // What every sample will print, for the notice in the footer
    allChosenItemIDs(): string[] {
      return this.states.flatMap((x) => x.itemIDs)
    },

    groupKey(): string {
      return `${this.tatGroupID}__${this.reportTemplateID}`
    },

    savedOverrides(): Record<string, ISampleDescriptionOverride> {
      return this.order?.reportSampleDescriptionOverrides?.[this.groupKey] ?? {}
    },

    states(): IReportSampleDescriptionState[] {
      if (!this.order) return []
      return resolveReportSampleDescriptionStates({
        order: this.order,
        tatGroupID: this.tatGroupID,
        reportTemplateID: this.reportTemplateID,
        results: this.orderResults,
        allAnalyses: this.allAnalyses,
        allAnalysisTests: this.allAnalysisTests,
        systemLists: this.systemLists,
        reportDescriptionItems: this.allReportDescriptionItems,
        reportDescriptions: this.allReportDescriptions,
        overridesBySampleID: { ...this.savedOverrides, ...this.drafts },
        labAccountID: this.labAccount?.id ?? '',
      })
    },

    activeState(): IReportSampleDescriptionState | null {
      return (
        this.states.find((x) => x.sampleID === this.activeSampleID) ??
        this.states[0] ??
        null
      )
    },

    changedSampleIDs(): string[] {
      return Object.keys(this.drafts).filter(
        (sampleID) =>
          !isEqual(
            normalizeOverride(this.drafts[sampleID]),
            normalizeOverride(this.savedOverrides[sampleID])
          )
      )
    },
  },

  methods: {
    ...mapActions(useOrderStore, ['updateOrderReportSampleDescriptionOverride']),

    onSampleClick(sampleID: string): void {
      this.activeSampleID = sampleID
    },

    // Every edit — removing, adding, restoring, dragging — arrives as the sample's new final
    // list; the override that yields exactly that list is derived from it.
    setFinalList(finalItemIDs: string[]): void {
      const state = this.activeState
      if (!state) return
      this.drafts = {
        ...this.drafts,
        [state.sampleID]: buildSampleDescriptionOverride({
          autoItemIDs: state.autoItemIDs,
          previousOverride: { ...this.savedOverrides, ...this.drafts }[state.sampleID],
          finalItemIDs,
        }),
      }
    },

    formatDescriptionCount(count: number): string {
      if (count === 0) return this.$t('W: ingen beskrivelser')
      if (count === 1) return this.$t('W: 1 beskrivelse')
      return this.$t('W: {count} beskrivelser', { count })
    },

    async onConfirm(): Promise<void> {
      this.isSaving = true
      this.errorText = ''
      try {
        for (const sampleID of this.changedSampleIDs) {
          await this.updateOrderReportSampleDescriptionOverride(
            this.orderID,
            this.tatGroupID,
            this.reportTemplateID,
            sampleID,
            this.drafts[sampleID] as ISampleDescriptionOverride
          )
        }
        this.showSaveIcon = true
        setTimeout(() => {
          this.showSaveIcon = false
        }, this.defaultMessageSaveIconTime)
        useToast().success(this.$t('W: Beskrivelser gemt'))
        // Saved — nothing left to warn about on the way out.
        this.drafts = {}
        this.$emit('close')
      } catch (error) {
        // A lab-cloud failure was reported there; a transport failure never reached it.
        reportUnexpectedError(error, {
          operation: 'order-report-sample-descriptions-save',
          extra: { orderID: this.orderID },
        })
        this.errorText = this.$t('W: Der opstod en fejl')
        useToast().error(this.$t('error.common.standard') as string)
      } finally {
        this.isSaving = false
      }
    },
  },

  async created() {
    await Promise.all([
      useReportDescriptionStore().getReportDescriptions(),
      useReportDescriptionItemStore().getReportDescriptionItems(),
      fetchOrderAnalysisResults({
        labAccountID: this.labAccount?.id ?? '',
        orderID: this.orderID,
        operation: 'fetch-order-results-for-sample-descriptions',
      }).then((results) => {
        this.orderResults = results
      }),
    ])
    this.isLoadingResults = false

    // The drafts are the only unsaved state; the selected tab and the add box's search are not.
    this.unsavedGuard.register({
      isDirty: () => this.changedSampleIDs.length > 0,
      modalId: this.modalEntryID,
    })
  },

  beforeUnmount() {
    this.unsavedGuard.unregister()
  },
}
</script>
